import React from "react";
import {
  Box,
  Flex,
  Image,
  ListItem,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tooltip,
  Tr,
  UnorderedList,
} from "@chakra-ui/react";
import useGetOrderHistoryConfirmedList from "@/features/my-order/features/order-history/features/final-copy/hooks/query/useGetOrderHistoryConfirmedList";
import {
  FlattenedOrderHistoryProduct,
  OrderHistoryConfirmedProduct,
} from "@/features/my-order/features/order-history/features/final-copy/types";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { useSearchParams } from "react-router-dom";
import useFinalCopyCurrency from "@/features/my-order/features/order-history/features/final-copy/hooks/useFinalCopyCurrency";
import HelpTooltip from "@/shared/components/help-tooltip";
import { calcConfirmedCondition } from "@/features/my-order/features/order-history/features/final-copy/utils";

// 단가를 표시 통화 기준으로 먼저 반올림한 뒤 수량을 곱하기 위한 헬퍼.
// (KRW는 정수, 그 외 통화는 소수점 2자리로 반올림)
const roundUnitPriceToDisplay = (value: number, isKrw: boolean) =>
  isKrw ? Math.round(value) : Math.round(value * 100) / 100;

function OrderHistoryFinalCopyTable() {
  const { tCurrencyString, tTitle } = useI18nHelper();

  const [searchParams] = useSearchParams();
  const orderSheetSetId = searchParams.get("order_sheet_set_id");
  const isConfirmedOnly = searchParams.get("confirmedOnly") === "true";
  const { confirmedData } = useGetOrderHistoryConfirmedList(
    orderSheetSetId ? +orderSheetSetId : 0
  );

  // 라인시트세트 통화 기준(원화 라인시트면 원화 그대로), 외화면 스위치로 원화 환산
  const {
    displayCurrency: currency,
    isDisplayKrw: isKrw,
    exchangeCurrencyValue,
  } = useFinalCopyCurrency();

  const filteredProducts = isConfirmedOnly
    ? confirmedData.products.filter((product) =>
        product.options.some((option) => option.confirmed_quantity > 0)
      )
    : confirmedData.products;

  const totalConfirmedQuantity = filteredProducts.reduce((acc, product) => {
    return (
      acc +
      product.options.reduce((sum, opt) => sum + opt.confirmed_quantity, 0)
    );
  }, 0);

  // 표시 통화로 환산한 단가를 먼저 반올림한 뒤 수량을 곱해 합산한다.
  // (곱한 뒤 반올림하면 표시 단가 × 수량과 값이 어긋날 수 있음)
  const totalAmount = filteredProducts.reduce((acc, product) => {
    const confirmedQty = product.options.reduce(
      (sum, opt) => sum + opt.confirmed_quantity,
      0
    );
    const roundedUnitPrice = roundUnitPriceToDisplay(
      (product.confirmed_price?.amount || 0) * exchangeCurrencyValue,
      isKrw
    );
    return acc + roundedUnitPrice * confirmedQty;
  }, 0);

  // 수수료 계산 단위가 ORDER면 상품 단가에는 수수료가 빠져 있으므로 합계에만 반영한다.
  const commission = confirmedData.commission;
  const hasCommission =
    confirmedData.commission_type === "ORDER" && !!commission;
  const commissionAmount = hasCommission
    ? totalAmount * ((commission ?? 0) / 100)
    : 0;
  const finalTotalAmount = totalAmount + commissionAmount;

  const flattenedOptions = filteredProducts.flatMap((product) => {
    const { options, ...baseData } = product;
    return options.map((option) => ({
      ...baseData,
      ...option,
    }));
  });

  return (
    <TableContainer overflowY={"auto"} flex={1} minHeight={0} maxHeight={"70%"}>
      <Table variant={"simple"}>
        <Thead background={"#FCFCFB"} position={"sticky"} top={"-0.5px"}>
          <Tr textStyle={"body-sm-medium"} color={"#6F6F6C"} height={"36px"}>
            <CustomTh minWidth={"60px"}>{}</CustomTh>
            <CustomTh minWidth={"118px"}>
              {tTitle("order_history:confirm.model_name")}
            </CustomTh>
            <CustomTh minWidth={"118px"}>
              {tTitle("order_history:confirm.brand")}
            </CustomTh>
            <CustomTh minWidth={"118px"}>
              {tTitle("order_history:confirm.category")}
            </CustomTh>
            <CustomTh minWidth={"80px"} textAlign={"center"}>
              {tTitle("order_history:confirm.color")}
            </CustomTh>
            <CustomTh minWidth={"80px"} textAlign={"center"}>
              {tTitle("order_history:confirm.origin")}
            </CustomTh>
            <CustomTh minWidth={"60px"} textAlign={"center"}>
              {tTitle("order_history:confirm.size")}
            </CustomTh>
            <CustomTh minWidth={"104px"} textAlign={"end"}>
              {tTitle("order_history:confirm.supply_cost")}
            </CustomTh>
            <CustomTh minWidth={"80px"} textAlign={"center"}>
              <Flex gap={"2px"} justifyContent={"center"} alignItems={"center"}>
                <Text>{tTitle("order_history:confirm.condition")}</Text>
                <Box>
                  <HelpTooltip
                    text={
                      <>
                        <Text>가격 조건 안내</Text>
                        <UnorderedList>
                          <ListItem>C+ : 도매가(Cost) + 마진율(%)</ListItem>
                          <ListItem>C- : 도매가(Cost) - 할인율(%)</ListItem>
                          <ListItem>R- : 소매가(Retail) - 할인율(%)</ListItem>
                        </UnorderedList>
                      </>
                    }
                  />
                </Box>
              </Flex>
            </CustomTh>
            <CustomTh minWidth={"104px"} textAlign={"end"}>
              {tTitle("order_history:confirm.price_with_condition")}
            </CustomTh>
            <CustomTh minWidth={"104px"} textAlign={"center"}>
              {tTitle("order_history:quantity")}(
              {tTitle("order_history:order/confirm")})
            </CustomTh>
            <CustomTh minWidth={"128px"} textAlign={"end"}>
              {tTitle("total")}
            </CustomTh>
          </Tr>
        </Thead>
        <Tbody>
          {flattenedOptions.map((item, index) => (
            <TableBodyRow
              key={`${item.sku}-${index}`}
              data={item}
              exchangeCurrencyValue={exchangeCurrencyValue}
            />
          ))}
        </Tbody>
        <Tfoot
          position={"sticky"}
          bottom={"-0.5px"}
          background={"#FCFCFB"}
          zIndex={1}
          boxShadow={"0 -0.5px 0 0 #D4D4D1"}
        >
          <Tr textStyle={"body-sm-bold"} height={"36px"}>
            <Td
              colSpan={10}
              textAlign={"end"}
              color={"#6F6F6C"}
              padding={"0px 12px"}
              textStyle={"body-sm-medium"}
              boxShadow={"inset 0.5px 0 0 0 #D4D4D1"}
            >
              {tTitle("total")}
            </Td>
            <Td
              textAlign={"center"}
              padding={"0px 8px"}
              background={"#E2F8FF"}
              color={"#1272EF"}
            >
              {totalConfirmedQuantity}
            </Td>
            <Td
              textAlign={"end"}
              padding={"0px 8px"}
              background={"#E2F8FF"}
              color={"#1272EF"}
            >
              {tCurrencyString(totalAmount, {
                type: "SYMBOL",
                code: currency,
              })}
            </Td>
          </Tr>
          {hasCommission && (
            <>
              <Tr textStyle={"body-sm-bold"} height={"36px"}>
                <Td
                  colSpan={11}
                  textAlign={"end"}
                  color={"#6F6F6C"}
                  padding={"0px 12px"}
                  textStyle={"body-sm-medium"}
                  boxShadow={"inset 0.5px 0 0 0 #D4D4D1"}
                >
                  {`${tTitle("order_history:confirm.commission")} (${commission}%)`}
                </Td>
                <Td
                  textAlign={"end"}
                  padding={"0px 8px"}
                  background={"#E2F8FF"}
                  color={"#1272EF"}
                >
                  {tCurrencyString(commissionAmount, {
                    type: "SYMBOL",
                    code: currency,
                  })}
                </Td>
              </Tr>
              <Tr textStyle={"body-sm-bold"} height={"36px"}>
                <Td
                  colSpan={11}
                  textAlign={"end"}
                  color={"#444444"}
                  padding={"0px 12px"}
                  boxShadow={"inset 0.5px 0 0 0 #D4D4D1"}
                >
                  {tTitle("order_history:confirm.final_total")}
                </Td>
                <Td
                  textAlign={"end"}
                  padding={"0px 8px"}
                  background={"#E2F8FF"}
                  color={"#1272EF"}
                >
                  {tCurrencyString(finalTotalAmount, {
                    type: "SYMBOL",
                    code: currency,
                  })}
                </Td>
              </Tr>
            </>
          )}
        </Tfoot>
      </Table>
    </TableContainer>
  );
}

export default OrderHistoryFinalCopyTable;

interface TableBodyRowProps {
  data: FlattenedOrderHistoryProduct;
  exchangeCurrencyValue: number;
}

const TableBodyRow = ({ data, exchangeCurrencyValue }: TableBodyRowProps) => {
  const { tCurrencyString } = useI18nHelper();

  const { displayCurrency: currency, isDisplayKrw: isKrw } =
    useFinalCopyCurrency();

  const {
    name: size,
    confirmed_quantity,
    order_quantity,
    image_url,
    made_in,
    color_name,
    confirmed_price_adjusted_by,
  } = data;

  const getQuantityBackground = () => {
    if (order_quantity === confirmed_quantity) return "#FCFCFB";
    if (confirmed_quantity === 0) return "#FFEEEE";
    return "#FFF7CC";
  };

  const conditionValue = calcConfirmedCondition(confirmed_price_adjusted_by);

  // 단가 열과 행 합계가 동일한 반올림 단가를 쓰도록 한 번만 계산한다.
  const roundedUnitPrice = roundUnitPriceToDisplay(
    (data.confirmed_price?.amount || 0) * exchangeCurrencyValue,
    isKrw
  );

  return (
    <Tr
      textStyle={"body-sm-medium"}
      color={"#444444"}
      background={"#FFFFFF"}
      _hover={{
        background: "#F7F9FA",
      }}
    >
      <CustomTd paddingX={"12px"}>
        <Flex justifyContent={"center"}>
          <Tooltip
            hasArrow={false}
            placement={"right"}
            label={
              <Image
                src={image_url}
                alt={data.sku}
                width={"200px"}
                height={"200px"}
                boxShadow={"0 0 0 0.5px #8F8F8C"}
                background={"#FFFFFF"}
                fallbackSrc={"/icons-v1_1/null.png"}
              />
            }
          >
            <Image
              src={image_url}
              alt={data.sku}
              width={"40px"}
              height={"40px"}
              boxShadow={"0 0 0 0.5px #8F8F8C"}
              borderRadius={"4px"}
              fallbackSrc={"/icons-v1_1/null.png"}
            />
          </Tooltip>
        </Flex>
      </CustomTd>
      <CustomTd paddingX={"8px"}>
        <Text>{data.sku}</Text>
      </CustomTd>
      <CustomTd paddingX={"8px"}>
        <Text>{data.brand_name}</Text>
      </CustomTd>
      <CustomTd paddingX={"8px"}>
        <Text>{data.category ?? "-"}</Text>
      </CustomTd>
      <CustomTd paddingX={"8px"} textAlign={"center"}>
        <Text>{color_name ?? "-"}</Text>
      </CustomTd>
      <CustomTd paddingX={"8px"} textAlign={"center"}>
        <Text>{made_in ?? "-"}</Text>
      </CustomTd>
      <CustomTd paddingX={"8px"} textAlign={"center"}>
        <Text>{size}</Text>
      </CustomTd>
      <CustomTd paddingX={"8px"} textAlign={"end"}>
        <Text>
          {tCurrencyString(
            (data.confirmed_price_without_condition?.amount ?? 0) *
              exchangeCurrencyValue,
            {
              type: "SYMBOL",
              code: currency,
            }
          )}
        </Text>
      </CustomTd>
      <CustomTd paddingX={"8px"} textAlign={"center"}>
        <Text color={"#1272EF"}>{conditionValue}</Text>
      </CustomTd>
      <CustomTd paddingX={"8px"} textAlign={"end"}>
        <Text>
          {tCurrencyString(roundedUnitPrice, {
            type: "SYMBOL",
            code: currency,
          })}
        </Text>
      </CustomTd>
      <CustomTd
        paddingX={"8px"}
        textAlign={"center"}
        background={getQuantityBackground()}
      >
        <Flex gap={"3px"} justifyContent={"center"} alignItems={"center"}>
          <Text>{order_quantity}</Text>
          <Text lineHeight={1}>→</Text>
          <Text color={"#1272EF"}>{confirmed_quantity}</Text>
        </Flex>
      </CustomTd>
      <CustomTd paddingX={"8px"} textAlign={"end"}>
        <Text textStyle={"body-sm-bold"} color={"#1272EF"}>
          {tCurrencyString(roundedUnitPrice * confirmed_quantity, {
            type: "SYMBOL",
            code: currency,
          })}
        </Text>
      </CustomTd>
    </Tr>
  );
};

type CustomThProps = React.ComponentProps<typeof Th>;
type CustomTdProps = React.ComponentProps<typeof Td>;

const CustomTh = ({ children, ...props }: CustomThProps) => {
  return (
    <Th
      padding={"8px"}
      minWidth={"60px"}
      border={"none"}
      boxShadow={"inset 0 -0.5px 0 0 #D4D4D1"}
      {...props}
    >
      {children}
    </Th>
  );
};

const CustomTd = ({ children, ...props }: CustomTdProps) => {
  return (
    <Td
      boxShadow={"inset 0 -0.5px 0 0 #D4D4D1"}
      border={"none"}
      height={"48px"}
      {...props}
    >
      {children}
    </Td>
  );
};
